/**
 * @see https://umijs.org/docs/max/access#access
 * Sistema de control de acceso basado en permisos por mÃ³dulos
 * SECURITY: Este sistema implementa una polÃ­tica de "denegar por defecto"
 */
export default function access(initialState: {
  currentUser?: any;
  permissions?: any;
  loading?: boolean;
} | undefined) {
  const { currentUser, permissions, loading } = initialState ?? {};

  const getCurrentRouteParams = () => {
    if (typeof window === 'undefined') return new URLSearchParams();

    const hash = window.location.hash || '';
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex >= 0) {
      return new URLSearchParams(hash.slice(hashQueryIndex + 1));
    }

    return new URLSearchParams(window.location.search || '');
  };

  const hasLegacyClientKanbanDeepLink = () => {
    const params = getCurrentRouteParams();
    const rawClientId = params.get('client') || params.get('clientId');
    const rawProjectId = params.get('project') || params.get('projectId');
    const clientId = Number(rawClientId || 0);
    const projectId = Number(rawProjectId || 0);

    return (
      (Number.isFinite(clientId) && clientId > 0)
      || (Number.isFinite(projectId) && projectId > 0)
    );
  };

  const hasModuleAccess = (moduleName: string) => {
    if (loading) return false;
    if (!currentUser) return false;

    const permissionsLoaded = (currentUser as any)?.permissionsLoaded;
    if (permissionsLoaded === false) {
      return false;
    }

    if (!permissions || Object.keys(permissions).length === 0) {
      return false;
    }

    const modulePerms =
      permissions[moduleName] ||
      permissions[moduleName === 'Consulta' ? 'Consultas' : moduleName === 'Consultas' ? 'Consulta' : ''];

    if (!modulePerms) return false;

    return modulePerms.hasAnyPermission === true;
  };

  const hasExplicitRouteAccess = (route: any) => {
    // SECURITY: Si aÃºn estÃ¡ cargando el estado inicial, DENEGAR ACCESO
    if (loading) {
      return false;
    }

    // SECURITY: Si no hay informaciÃ³n del usuario, DENEGAR ACCESO
    if (!currentUser) {
      return false;
    }

    // SECURITY: Si no hay moduleName definido en la ruta, DENEGAR ACCESO
    // Esto fuerza que todas las rutas protegidas tengan moduleName explÃ­cito
    if (!route?.moduleName) {
      return false;
    }

    // SECURITY: Verificar si los permisos se cargaron exitosamente
    const permissionsLoaded = (currentUser as any)?.permissionsLoaded;
    if (permissionsLoaded === false) {
      // Solo permitir acceso muy limitado para admin cuando los permisos fallan
      if (currentUser.fk_rol === 1) {
        const criticalAdminModules = ['Panel de AdministraciÃ³n', 'Usuario', 'Rol', 'Permiso', 'MÃ³dulo'];
        if (criticalAdminModules.includes(route.moduleName)) {
          return true;
        }
      }

      return false;
    }

    // SECURITY: Si no hay permisos o estÃ¡n vacÃ­os, DENEGAR ACCESO
    if (!permissions || Object.keys(permissions).length === 0) {
      return false;
    }

    // Verificar si el usuario tiene algÃºn permiso en el mÃ³dulo especÃ­fico
    const modulePerms =
      permissions[route.moduleName] ||
      permissions[route.moduleName === 'Consulta' ? 'Consultas' : route.moduleName === 'Consultas' ? 'Consulta' : ''];
    if (!modulePerms) {
      return false;
    }

    return modulePerms.hasAnyPermission === true;
  };

  return {
    // Control de acceso bÃ¡sico para admin
    canAdmin: currentUser && currentUser.fk_rol === 1,

    // Control de acceso dinÃ¡mico basado en permisos
    canAccess: (route: any) => hasExplicitRouteAccess(route),

    // Compatibilidad para enlaces legacy que todavÃ­a aterrizan en /ClientKanban
    // aunque el destino funcional sea el hilo comercial.
    canAccessKanbanCompat: (route: any) => {
      if (hasExplicitRouteAccess(route)) return true;

      const isClientKanbanRoute =
        route?.path === '/ClientKanban' || route?.moduleName === 'Kanban de Clientes';

      if (!isClientKanbanRoute) return false;

      return hasModuleAccess('Proyectos') && hasLegacyClientKanbanDeepLink();
    },

    canAccessCreatedOrders: () =>
      currentUser?.fk_rol === 1
      || hasModuleAccess('Pedidos')
      || hasModuleAccess('Proyectos'),

    // El rol 1 es Administrador, que es justamente el perfil al que apuntan las
    // secciones "solo admin" del tablero (Seguimiento comerciales, Proyección y
    // facturación, Presupuesto comercial). Excluirlo dejaba esas secciones
    // inalcanzables y devolvía 403 a quien más las necesita. Mismo criterio que
    // canAccessCreatedOrders: admin entra siempre, el resto por permiso de módulo.
    canAccessExecutiveDashboard: () =>
      !loading
      && !!currentUser
      && (Number(currentUser?.fk_rol) === 1 || hasModuleAccess('Proyectos')),
  };
}
